/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {get} from '@ion/core/src/environment/environment';
import {IApplicationFeatures} from '@ion/core/src/security/ApplicationFeatures';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink, Entity} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';

/**
 * Convenience hook which is used to determines if the `LMUConfigDeviceFilePrefetch`
 * component is to be rendered or not based on the provided arguments.
 *
 * If the id of the provided `globalGroup` is the root Account Id, and / or if the
 * `enable` argument is false, returns `false`, as the `LMUConfigDeviceFilePrefetch`
 * component is to be omitted in each case, otherwise, returns `true`, indicating
 * that the `LMUConfigDeviceFilePrefetch` component should be rendered.
 *
 * ```typescript
 *  import {useEnableDeviceFilePrefetch} from '@ion/applications/src/components/jobs';
 *  ...
 *
 *  const isEnabled = useEnableDeviceFilePrefetch(
 *    enable, globalGroup, Features
 *  );
 *  ...
 *
 *  <ConditionalRender condition={isEnabled}>
 *    <LMUConfigDeviceFilePrefetch accountId={entityId(globalGroup)}
 *      includeSubAccounts={viewSubGroups} />
 *  </ConditionalRender>
 * ```
 *
 * @param   enable when `true`, device file pre-fetching will be enabled if all other
 *          constraints are passed. This argument is generally to be set based on a
 *          prop passed down to the component which uses the hook, as while certain
 *          applications will have device file features, not all will, thus we want
 *          to ensure no prefetching is performed unless explicitly required.
 * @param   globalGroup a reference to the currently selected global group. If the
 *          group is the root account, pre-fetching will be disabled.
 * @param   features a reference to the application `Features` API from which the
 *          required features are verified.
 * @returns `true` if Device File Prefetching and caching is to be enabled, otherwise,
 *          `false`.
 */
export const useEnableDeviceFilePrefetch = (
  enable: boolean, globalGroup: AccountLink, features: IApplicationFeatures): boolean => {
  const allowed = useMemo(() => {
    if (enable) {
      return (
        features.hasPermission(Entity.job, Operation.Create) &&
        features.hasPermission(Entity.lmuscript, Operation.Assign) &&
        features.hasPermission(Entity.lmuscript, Operation.Read)
      );
    }
    return false;
  }, [enable]);

  const isEnabled: boolean = useMemo(() => {
    if (enable && allowed) {
      return entityId(globalGroup) !== parseInt(get('rootAccountId'));
    }
    return false;
  }, [enable, globalGroup]);
  return isEnabled;
};
export default useEnableDeviceFilePrefetch;
